import { useEffect, useState } from "react";
import { toast } from "sonner";
import { LogOut, Plus, Trash2, X } from "lucide-react";
import { API, formatApiError } from "@/lib/api";
import { useSEO } from "@/lib/useSEO";
import { INSIGHT_CATEGORIES, PANEL_FILTERS } from "@/data/content";
import { Monogram } from "@/components/Navbar";

const ENQUIRY_STATUSES = ["New", "In Review", "Contacted", "Closed"];
const ADVOCATE_STATUSES = ["draft", "verification_pending", "approved", "published", "hidden", "archived"];

const Login = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await API.post("/auth/login", { email, password });
      onLogin(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-ink px-5">
      <form data-testid="admin-login-form" onSubmit={submit} className="w-full max-w-md border border-gold/25 bg-navy p-10">
        <div className="flex justify-center"><Monogram /></div>
        <p className="mono-label mt-8 text-center">Office Administration</p>
        <h1 className="mt-3 text-center font-serif text-3xl font-medium text-ivory">Administrator Sign-In</h1>
        <div className="mt-10 space-y-6">
          <div>
            <label className="field-label" htmlFor="admin-email">Email</label>
            <input id="admin-email" data-testid="admin-email-input" type="email" className="field-input-dark" value={email} onChange={(e) => setEmail(e.target.value)} required />
          </div>
          <div>
            <label className="field-label" htmlFor="admin-password">Password</label>
            <input id="admin-password" data-testid="admin-password-input" type="password" className="field-input-dark" value={password} onChange={(e) => setPassword(e.target.value)} required />
          </div>
          <button data-testid="admin-login-submit-btn" disabled={busy} className="btn-outline-gold w-full justify-center disabled:opacity-40">
            {busy ? "Signing in…" : "Sign In"}
          </button>
        </div>
      </form>
    </div>
  );
};

const Enquiries = () => {
  const [items, setItems] = useState([]);
  const load = () => API.get("/admin/enquiries").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const setStatus = async (reference, status) => {
    try {
      await API.patch(`/admin/enquiries/${reference}`, { status });
      toast.success(`Marked ${reference} as ${status}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div data-testid="admin-enquiries-tab">
      {items.length === 0 && <p className="py-16 text-center font-serif text-2xl text-navy/50">No enquiries received yet.</p>}
      <div className="space-y-4">
        {items.map((e) => (
          <div key={e.reference} data-testid={`admin-enquiry-${e.reference}`} className="border border-navy/15 bg-warm p-6">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <p className="font-mono text-xs tracking-[0.15em] text-gold">{e.reference}</p>
                <p className="mt-2 font-serif text-xl font-medium text-navy">{e.full_name}</p>
                <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.15em] text-slate">
                  {e.practice_area} · {e.urgency} · {e.created_at?.slice(0, 10)}
                </p>
              </div>
              <select
                data-testid={`admin-enquiry-status-${e.reference}`}
                value={e.status}
                onChange={(ev) => setStatus(e.reference, ev.target.value)}
                className="field-input !w-auto !py-2 text-xs"
                aria-label={`Status for ${e.reference}`}
              >
                {ENQUIRY_STATUSES.map((s) => <option key={s}>{s}</option>)}
              </select>
            </div>
            <div className="mt-4 grid gap-2 text-sm text-navy/65 sm:grid-cols-2">
              <span>{e.mobile} · {e.email}</span>
              <span>Court: {e.court_forum || "—"} · Stage: {e.case_stage || "—"}</span>
              <span>Opposite party: {e.opposite_party || "—"}</span>
              <span>Hearing: {e.next_hearing || "—"} · Deadline: {e.deadline || "—"}</span>
            </div>
            <p className="mt-4 border-t border-navy/10 pt-4 text-sm leading-relaxed text-navy/60">{e.description}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

const emptyInsight = { title: "", category: "Civil Procedure", excerpt: "", reading_time: 5, author: "Smart Legal Solutions — Research Desk", designation: "", date_reviewed: new Date().toISOString().slice(0, 10), sections: [], sources: [], status: "draft" };

const InsightEditor = ({ initial, onSave, onCancel }) => {
  const [f, setF] = useState(initial);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const setSection = (i, k, v) => set("sections", f.sections.map((s, x) => (x === i ? { ...s, [k]: v } : s)));

  return (
    <div data-testid="admin-insight-editor" className="border border-gold/40 bg-ivory p-6 sm:p-8">
      <div className="grid gap-5 sm:grid-cols-2">
        <div className="sm:col-span-2">
          <label className="field-label">Title</label>
          <input data-testid="insight-title-input" className="field-input" value={f.title} onChange={(e) => set("title", e.target.value)} />
        </div>
        <div>
          <label className="field-label">Category</label>
          <select data-testid="insight-category-select" className="field-input" value={f.category} onChange={(e) => set("category", e.target.value)}>
            {INSIGHT_CATEGORIES.slice(1).map((c) => <option key={c}>{c}</option>)}
          </select>
        </div>
        <div>
          <label className="field-label">Status</label>
          <select data-testid="insight-status-select" className="field-input" value={f.status} onChange={(e) => set("status", e.target.value)}>
            <option value="draft">Draft</option>
            <option value="published">Published</option>
          </select>
        </div>
        <div>
          <label className="field-label">Reading Time (min)</label>
          <input className="field-input" type="number" min="1" value={f.reading_time} onChange={(e) => set("reading_time", Number(e.target.value))} />
        </div>
        <div>
          <label className="field-label">Date Reviewed</label>
          <input data-testid="insight-date-input" className="field-input" type="date" value={f.date_reviewed} onChange={(e) => set("date_reviewed", e.target.value)} />
        </div>
        <div className="sm:col-span-2">
          <label className="field-label">Excerpt</label>
          <textarea className="field-input" rows={2} value={f.excerpt} onChange={(e) => set("excerpt", e.target.value)} />
        </div>
      </div>
      <div className="mt-6">
        <div className="flex items-center justify-between">
          <label className="field-label !mb-0">Sections</label>
          <button data-testid="insight-add-section-btn" onClick={() => set("sections", [...f.sections, { heading: "", body: "" }])} className="btn-outline-dark !px-4 !py-2"><Plus size={12} /> Add Section</button>
        </div>
        <div className="mt-4 space-y-4">
          {f.sections.map((s, i) => (
            <div key={i} className="border border-navy/15 p-4">
              <div className="flex items-center justify-between gap-4">
                <input className="field-input" placeholder="Section heading" value={s.heading} onChange={(e) => setSection(i, "heading", e.target.value)} data-testid={`insight-section-heading-${i}`} />
                <button onClick={() => set("sections", f.sections.filter((_, x) => x !== i))} aria-label="Remove section" className="text-slate hover:text-oxblood"><Trash2 size={15} /></button>
              </div>
              <textarea className="field-input mt-3" rows={3} placeholder="Section body" value={s.body} onChange={(e) => setSection(i, "body", e.target.value)} data-testid={`insight-section-body-${i}`} />
            </div>
          ))}
        </div>
      </div>
      <div className="mt-8 flex gap-3">
        <button data-testid="insight-save-btn" onClick={() => onSave(f)} className="btn-outline-dark">Save Article</button>
        <button data-testid="insight-cancel-btn" onClick={onCancel} className="font-mono text-[11px] uppercase tracking-[0.2em] text-slate hover:text-oxblood">Cancel</button>
      </div>
    </div>
  );
};

const InsightsAdmin = () => {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = () => API.get("/admin/insights").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async (f) => {
    try {
      if (editing.slug) await API.put(`/admin/insights/${editing.slug}`, f);
      else await API.post("/admin/insights", f);
      toast.success("Article saved");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const remove = async (slug) => {
    if (!window.confirm("Delete this article permanently?")) return;
    await API.delete(`/admin/insights/${slug}`);
    load();
  };

  return (
    <div data-testid="admin-insights-tab">
      {editing ? (
        <InsightEditor initial={editing} onSave={save} onCancel={() => setEditing(null)} />
      ) : (
        <>
          <button data-testid="admin-new-insight-btn" onClick={() => setEditing({ ...emptyInsight })} className="btn-outline-dark mb-6"><Plus size={12} /> New Article</button>
          <div className="space-y-3">
            {items.map((a) => (
              <div key={a.slug} data-testid={`admin-insight-row-${a.slug}`} className="flex flex-wrap items-center justify-between gap-4 border border-navy/15 bg-warm px-5 py-4">
                <div>
                  <p className="font-serif text-lg font-medium text-navy">{a.title}</p>
                  <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.15em] text-slate">
                    {a.category} · Reviewed {a.date_reviewed} ·{" "}
                    <span className={a.status === "published" ? "text-gold" : "text-oxblood"}>{a.status}</span>
                  </p>
                </div>
                <div className="flex gap-2">
                  <button data-testid={`admin-insight-edit-${a.slug}`} onClick={() => setEditing({ ...emptyInsight, ...a })} className="border border-navy/25 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.15em] text-navy hover:border-gold">Edit</button>
                  <button data-testid={`admin-insight-delete-${a.slug}`} onClick={() => remove(a.slug)} className="border border-oxblood/30 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.15em] text-oxblood hover:bg-oxblood hover:text-ivory">Delete</button>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

const emptyAdvocate = { name: "", designation: "Panel Advocate", enrolment: "", year_enrolled: "", practice_areas: [], courts: [], languages: [], qualifications: "", city: "Vijayawada", experience_years: "", bio: "", status: "draft" };

const AdvocateEditor = ({ initial, onSave, onCancel }) => {
  const [f, setF] = useState({
    ...initial,
    practice_areas: initial.practice_areas || [],
    courts: initial.courts || [],
    languages: initial.languages || [],
  });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const csv = (v) => v.split(",").map((x) => x.trim()).filter(Boolean);

  return (
    <div data-testid="admin-advocate-editor" className="border border-gold/40 bg-ivory p-6 sm:p-8">
      <div className="grid gap-5 sm:grid-cols-2">
        <div>
          <label className="field-label">Full Legal Name</label>
          <input data-testid="advocate-name-input" className="field-input" value={f.name} onChange={(e) => set("name", e.target.value)} />
        </div>
        <div>
          <label className="field-label">Designation</label>
          <input className="field-input" value={f.designation} onChange={(e) => set("designation", e.target.value)} />
        </div>
        <div>
          <label className="field-label">Enrolment Number</label>
          <input className="field-input" value={f.enrolment} onChange={(e) => set("enrolment", e.target.value)} />
        </div>
        <div>
          <label className="field-label">Year of Enrolment</label>
          <input className="field-input" value={f.year_enrolled} onChange={(e) => set("year_enrolled", e.target.value)} />
        </div>
        <div>
          <label className="field-label">Qualifications</label>
          <input className="field-input" value={f.qualifications} onChange={(e) => set("qualifications", e.target.value)} />
        </div>
        <div>
          <label className="field-label">Years of Practice (if verified)</label>
          <input className="field-input" type="number" min="0" value={f.experience_years ?? ""} onChange={(e) => set("experience_years", e.target.value === "" ? null : Number(e.target.value))} />
        </div>
        <div>
          <label className="field-label">Primary City</label>
          <input className="field-input" value={f.city} onChange={(e) => set("city", e.target.value)} />
        </div>
        <div>
          <label className="field-label">Status</label>
          <select data-testid="advocate-status-select" className="field-input" value={f.status} onChange={(e) => set("status", e.target.value)}>
            {ADVOCATE_STATUSES.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
          </select>
        </div>
        <div>
          <label className="field-label">Practice Areas (comma separated)</label>
          <input data-testid="advocate-areas-input" className="field-input" value={f.practice_areas.join(", ")} onChange={(e) => set("practice_areas", csv(e.target.value))} list="panel-area-options" />
          <datalist id="panel-area-options">{PANEL_FILTERS.slice(1).map((o) => <option key={o} value={o} />)}</datalist>
        </div>
        <div>
          <label className="field-label">Courts & Forums (comma separated)</label>
          <input className="field-input" value={f.courts.join(", ")} onChange={(e) => set("courts", csv(e.target.value))} />
        </div>
        <div>
          <label className="field-label">Languages (comma separated)</label>
          <input className="field-input" value={f.languages.join(", ")} onChange={(e) => set("languages", csv(e.target.value))} />
        </div>
        <div>
          <label className="field-label">Panel Strength Verified On</label>
          <input className="field-input" type="date" value={f.panel_verified_on || ""} onChange={(e) => set("panel_verified_on", e.target.value)} />
        </div>
        <div className="sm:col-span-2">
          <label className="field-label">Short Professional Introduction</label>
          <textarea className="field-input" rows={3} value={f.bio} onChange={(e) => set("bio", e.target.value)} />
        </div>
      </div>
      <p className="mt-5 font-mono text-[10px] leading-relaxed tracking-[0.06em] text-slate">
        A profile becomes public only when its status is set to &ldquo;published&rdquo; after manual verification and
        consent. Do not publish unverified designations or experience.
      </p>
      <div className="mt-6 flex gap-3">
        <button data-testid="advocate-save-btn" onClick={() => onSave(f)} className="btn-outline-dark">Save Profile</button>
        <button onClick={onCancel} className="font-mono text-[11px] uppercase tracking-[0.2em] text-slate hover:text-oxblood">Cancel</button>
      </div>
    </div>
  );
};

const AdvocatesAdmin = () => {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = () => API.get("/admin/advocates").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async (f) => {
    try {
      if (editing.id) await API.put(`/admin/advocates/${editing.id}`, f);
      else await API.post("/admin/advocates", f);
      toast.success("Profile saved");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this profile permanently?")) return;
    await API.delete(`/admin/advocates/${id}`);
    load();
  };

  return (
    <div data-testid="admin-advocates-tab">
      {editing ? (
        <AdvocateEditor initial={editing} onSave={save} onCancel={() => setEditing(null)} />
      ) : (
        <>
          <button data-testid="admin-new-advocate-btn" onClick={() => setEditing({ ...emptyAdvocate })} className="btn-outline-dark mb-6"><Plus size={12} /> New Advocate Profile</button>
          <div className="space-y-3">
            {items.map((a) => (
              <div key={a.id} data-testid={`admin-advocate-row-${a.id}`} className="flex flex-wrap items-center justify-between gap-4 border border-navy/15 bg-warm px-5 py-4">
                <div>
                  <p className="font-serif text-lg font-medium text-navy">{a.name}</p>
                  <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.15em] text-slate">
                    {(a.practice_areas || []).slice(0, 2).join(" · ")} ·{" "}
                    <span className={a.status === "published" ? "text-gold" : "text-oxblood"}>{a.status.replace(/_/g, " ")}</span>
                  </p>
                </div>
                <div className="flex gap-2">
                  <button data-testid={`admin-advocate-edit-${a.id}`} onClick={() => setEditing(a)} className="border border-navy/25 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.15em] text-navy hover:border-gold">Edit</button>
                  <button data-testid={`admin-advocate-delete-${a.id}`} onClick={() => remove(a.id)} className="border border-oxblood/30 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.15em] text-oxblood hover:bg-oxblood hover:text-ivory">Delete</button>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default function Admin() {
  useSEO("Office Administration | Smart Legal Solutions", "Restricted administrative area.");
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [tab, setTab] = useState("enquiries");

  useEffect(() => {
    API.get("/auth/me")
      .then((r) => setUser(r.data))
      .catch(() => setUser(false))
      .finally(() => setChecking(false));
  }, []);

  const logout = async () => {
    await API.post("/auth/logout").catch(() => {});
    setUser(false);
  };

  if (checking) return <div className="min-h-screen bg-ink" />;
  if (!user) return <Login onLogin={setUser} />;

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-ivory pt-10">
      <div className="mx-auto max-w-6xl px-5 pb-24 lg:px-10">
        <div className="flex flex-wrap items-center justify-between gap-6 border-b border-navy/10 pb-8">
          <div className="flex items-center gap-4">
            <Monogram />
            <div>
              <p className="mono-label-dark">Smart Legal Solutions</p>
              <h1 className="mt-1 font-serif text-3xl font-medium text-navy">Office Administration</h1>
            </div>
          </div>
          <div className="flex items-center gap-5">
            <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-slate">{user.email}</span>
            <button data-testid="admin-logout-btn" onClick={logout} className="btn-outline-dark !px-4 !py-2">
              <LogOut size={12} /> Sign Out
            </button>
          </div>
        </div>

        <div className="mt-8 flex gap-2" role="tablist">
          {[
            ["enquiries", "Enquiries"],
            ["insights", "Legal Insights CMS"],
            ["advocates", "Advocate Panel"],
          ].map(([key, label]) => (
            <button
              key={key}
              role="tab"
              aria-selected={tab === key}
              data-testid={`admin-tab-${key}`}
              onClick={() => setTab(key)}
              className={`border px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.18em] transition-colors ${tab === key ? "border-navy bg-navy text-ivory" : "border-navy/25 text-navy/60 hover:border-gold"}`}
            >
              {label}
            </button>
          ))}
        </div>

        <div className="mt-8">
          {tab === "enquiries" && <Enquiries />}
          {tab === "insights" && <InsightsAdmin />}
          {tab === "advocates" && <AdvocatesAdmin />}
        </div>
      </div>
    </div>
  );
}
